import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import {
    getOrder,
    copyOrder,
    updateOrder,
    PayloadOrderHeaderViewRow,
    updateAvailabilityMode,
} from '../../features/order/orderSlice'
import styles from './SearchQuotesModal.module.scss'
import {
    BUTTON_TYPES,
    AWCButton,
    FastQuoteInput,
    RadioButtonGroup,
} from '../../GlobalComponents/Inputs/Inputs'
import { Scrollbars } from 'react-custom-scrollbars-2'
import { FixedSizeList as List } from 'react-window'
import {
    CarrierServiceOptionsReturnPayload,
    fetchCarrierServiceOptions,
    fetchFreightTerms,
    fetchQuoteUser,
    fetchWarehouse,
    FreightTermsReturnPayload,
    GetOrderReturnPayload,
    OrderHistoryReturn,
    QuoteUserInfo,
    WarehouseReturnPayload,
} from '../../features/order/orderAPI'
import clsx from 'clsx'
import { LOADING_STATES } from '../../features/meta/metaSlice'
import { FilterIcon } from '../../GlobalComponents/_icons/Icons'
import LineItems from './LineItems'
import { fetchGetContact } from '../../features/customer/customerAPI'
import { COUNTRY_CODES } from '../../consts'
import { HoverIcon } from '../../GlobalComponents/_icons/HoverIcon'
import { Dialog } from '../../GlobalComponents/GenericModals/GenericModals'
import { getBaseURL } from '../../GlobalComponents/URLs'
import { AutoSizer } from 'react-virtualized'
import { TextOverflow } from '../../GlobalComponents/_misc/TextOverflow'
import useMeasure from 'react-use-measure'
import { useMediaQuery } from 'react-responsive'

interface LoadOptionsDialogProps {
    onSubmit({
        UpdateAvailability,
        UpdatePricing,
    }: {
        UpdateAvailability: boolean
        UpdatePricing: boolean
    }): void
    title: string
    close(): void
    canOpenInFQ: boolean
    errorText: string
    availabilityMode: string
}
let loadAttempt: number = 0
const LoadOptionsDialog = (props: LoadOptionsDialogProps) => {
    const { onSubmit, close, title, canOpenInFQ, errorText, availabilityMode } = props
    const [UpdatePricing, setUpdatePricing] = useState(true)
    const [UpdateAvailability, setUpdateAvailability] = useState(true)
    const legacyAvailabilityModes = ['', 'A', 'B']
    return (
        <Dialog
            title={title}
            onCancel={close}
            onConfirm={() => onSubmit({ UpdateAvailability, UpdatePricing })}
        >
            <div style={{ marginTop: 30 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                    <div>Update Pricing?</div>

                    <RadioButtonGroup
                        defaultValue={true}
                        onChange={(newVal: boolean) => {
                            setUpdatePricing(newVal)
                        }}
                    />
                </div>
                <div style={{ fontWeight: 300, marginTop: 8 }}>
                    This update will be the suggested sell prices based upon current costs.
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 30 }}>
                    <div>Update Availability?</div>

                    <RadioButtonGroup
                        defaultValue={true}
                        onChange={(newVal: boolean) => {
                            setUpdateAvailability(newVal)
                        }}
                    />
                </div>

                <div style={{ fontWeight: 300, marginTop: 8 }}>
                    This will override any manually entered Estimated Delivery Dates.
                </div>
            </div>
            {!canOpenInFQ ? (
                <div style={{ color: 'red', marginTop: 10 }}>
                    Fast quote does not support all the line items on this quote. Quote will open in
                    legacy .NET.
                </div>
            ) : legacyAvailabilityModes.includes(availabilityMode.trim()) ? (
                <div style={{ color: 'red', marginTop: 10 }}>{errorText}</div>
            ) : (
                <></>
            )}
        </Dialog>
    )
}

interface UpdateClosedDialogProps {
    onNewQuote(): void
    onCancel(): void
}
const UpdateClosedDialog = (props: UpdateClosedDialogProps) => {
    const { onNewQuote, onCancel } = props
    return (
        <Dialog
            onCancel={onCancel}
            onConfirm={onNewQuote}
            confirmText={'New Quote'}
            title={'Closed Quotes & Orders cannot be updated'}
        >
            A New Quote can be generated with the same Bill of Materials
        </Dialog>
    )
}
interface PreviewHeaderProps {
    order: GetOrderReturnPayload
    type: string
    customer: CustomerRow
    toContact: undefined | ContactCustomerAccountCodeViewRow
    // TODO make this thing return undefined or contactviewrow
    toContactExtended: false | ContactViewRow
    // quoteUser: QuoteUserReturnPayload['User']
    quoteUser: QuoteUserInfo
    warehouse: WarehouseReturnPayload
    close(): void
    onSubmit(): void
}
const PreviewHeader = (props: PreviewHeaderProps) => {
    const {
        order,
        customer,
        toContact,
        toContactExtended,
        quoteUser,
        warehouse,
        close,
        onSubmit,
        type,
    } = props
    const hasValidPart = true
    const orderShipTo = order.OrderHeaderView[0]
    const reviewingQuoteOutput = true
    const orderHeader = order.OrderHeaderView
    const orderMiscInfo = order.OrderHeaderView[0]
    const currentRFQToContact = useAppSelector((state) => state.order.toContact)!
    const currentRFQToCustomer = useAppSelector((state) => state.order.currentCustomer)!
    const [loadOptionsOpen, setLoadOptionsOpen] = useState<
        | false
        | {
              OrderNumber: string
              ContactKey: number
              CustomerAccountCode: string
              mode: 'copy' | 'update'
          }
    >(false)
    const [updateClosedOpen, setUpdateClosedOpen] = useState<
        | false
        | {
              OrderNumber: string
              ContactKey: number
              CustomerAccountCode: string
          }
    >(false)
    const dispatch = useAppDispatch()

    // Shipping Method Details
    const [shippingCarrier, setShippingCarrier] = useState<string>('')
    const [shippingService, setShippingService] = useState<string>('')
    const [shippingTerms, setShippingTerms] = useState<string>('')
    const [shippingCollectAccount, setShippingCollectAccount] = useState<string>('')

    useEffect(() => {
        async function getShippingMethods() {
            let carrierServiceOptions: CarrierServiceOptionsReturnPayload =
                await fetchCarrierServiceOptions()
            let freightTerms: FreightTermsReturnPayload = await fetchFreightTerms()

            if (carrierServiceOptions && freightTerms) {
                const shipmentMethodDescription = orderHeader[0].ShipmentMethodDescription

                if (shipmentMethodDescription) {
                    const shipmentMethodSplit = shipmentMethodDescription?.split(';')
                    const carrierNameShort = shipmentMethodSplit![0]
                        ? shipmentMethodSplit[0].trim()
                        : ''
                    const serviceNameShort = shipmentMethodSplit[1]
                        ? shipmentMethodSplit[1].trim()
                        : ''
                    const carrierServiceName = carrierNameShort + ';' + serviceNameShort
                    const termsShort = shipmentMethodSplit[2] ? shipmentMethodSplit[2].trim() : ''
                    const collectAccountNumber = shipmentMethodSplit[3]
                        ? shipmentMethodSplit[3].trim()
                        : ''

                    carrierServiceOptions.forEach((carrier) => {
                        if (carrier.CarrierNameABR === carrierNameShort) {
                            carrier.CarrierServices.forEach((carrierService) => {
                                if (carrierService.CarrierServiceValue === carrierServiceName) {
                                    setShippingCarrier(carrier.CarrierName)
                                    setShippingService(carrierService.ServiceName)
                                }
                            })
                        }
                    })

                    freightTerms.forEach((terms) => {
                        if (terms.Terms === termsShort) {
                            setShippingTerms(terms.ShortDescription)
                            // account # can exist
                            if (termsShort === 'Col' || termsShort === '3rd') {
                                setShippingCollectAccount(collectAccountNumber)
                            }
                        }
                    })
                }
            }
        }
        getShippingMethods()
    }, [])

    // const canBeCopied = (header: PayloadOrderHeaderViewRow) => {
    //     let fastQuoteSupported = canBeOpenedInFQ(header, true)
    //     let isClosed = order.OrderHeaderView[0].ClosedStatus === true
    //     return isClosed
    // }
    const canBeOpenedInFQ = (header: PayloadOrderHeaderViewRow, forCopy: boolean) => {
        // can't have asssembly
        let notFQ = header.OrderDetailView.some((row) => row.IsAssembly)

        notFQ =
            notFQ || header.OrderDetailView.some((row) => row.OrderAssemblyDetailView.length > 0)

        notFQ = notFQ || header.ProgressBillingScheduleView.length > 0

        // or lockin PO
        notFQ = notFQ || header.OrderDetailView.some((row) => row.LockInPONumber)

        // or type 1 inventory parts
        notFQ =
            notFQ ||
            header.OrderDetailView.some((row) => row.metaInfo?.partInfo?.InventoryTypeKey !== 1)

        // not updating A or B in fq
        // if (!forCopy) {
        //     notFQ = notFQ || header.AvailabilityMode === 'A' || header.AvailabilityMode === 'B'
        // }
        // or partkey of 0
        notFQ = notFQ || header.OrderDetailView.some((row) => row.PartKey === 0)

        return !notFQ
    }
    const onUpdateClick = () => {
        // let isClosed = order.OrderHeaderView[0].ClosedStatus === true
        let canUpdate = type !== 'Open Quote'
        !canUpdate &&
            setLoadOptionsOpen({
                OrderNumber: order.OrderHeaderView[0].OrderNumber,
                ContactKey: currentRFQToContact.ContactKey,
                CustomerAccountCode: currentRFQToCustomer.AccountCode,
                mode: 'update',
            })
        canUpdate &&
            setUpdateClosedOpen({
                OrderNumber: order.OrderHeaderView[0].OrderNumber,
                ContactKey: currentRFQToContact.ContactKey,
                CustomerAccountCode: currentRFQToCustomer.AccountCode,
            })
    }

    const onNewClick = async () => {
        setLoadOptionsOpen({
            OrderNumber: order.OrderHeaderView[0].OrderNumber,
            ContactKey: currentRFQToContact.ContactKey,
            CustomerAccountCode: currentRFQToCustomer.AccountCode,
            mode: 'copy',
        })
    }

    // let isClosed = order.OrderHeaderView[0].ClosedStatus === true
    const legacyAvailabilityModes = ['', 'A', 'B']

    return (
        <div
            style={{
                width: '100%',
                boxSizing: 'border-box',
                display: 'grid',
                gridTemplateColumns:
                    'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.25fr) 240px',
                gridTemplateRows: 'auto 1px',
                paddingLeft: 15,
                paddingTop: 15,
                paddingRight: 5,
            }}
            className={styles.previewHeader}
        >
            {toContactExtended && (
                <div className={styles.toSection}>
                    <div className={styles.toRow}>To:</div>
                    <div className={styles.customerLine}>
                        {toContact?.FirstName.trim()} {toContact?.LastName.trim()}
                    </div>
                    <div className={styles.toLine}>{toContactExtended.CompanyName}</div>
                    <div style={{ color: 'var(--linkblue)' }}>
                        <TextOverflow text={toContact?.EmailAddress || ''} maxWidth={230} />
                    </div>
                    <div className={styles.toLine}>{toContactExtended.TelephoneNumber}</div>
                </div>
            )}
            {!toContactExtended && (
                <div className={styles.toSection}>
                    <div className={styles.toRow}>To:</div>
                </div>
            )}
            <div
                className={styles.tableHeader}
                style={{
                    display: 'inline-block',
                    gridColumn: '1/4',
                    gridRow: '2',
                    position: 'relative',
                }}
            >
                <div style={{ position: 'absolute', bottom: 10 }}>
                    <span>BOM Name: </span>
                    <span
                        style={{
                            fontWeight: 500,
                            marginLeft: 5,
                        }}
                    >
                        {order.OrderHeaderView[0].CartName}
                    </span>
                </div>
            </div>

            <div
                className={styles.tableHeader}
                style={{
                    display: 'inline-block',
                    gridColumn: '4',
                    gridRow: '2',
                    borderLeft: '2px solid #99999932',
                }}
            ></div>

            <div>
                <div className={styles.billToRow}>Bill to:</div>
                <div className={styles.customerLine}>
                    {customer.BillToName} ({customer.AccountCode.trim()})
                </div>
                <div className={styles.billToLine}>{customer.BillToAddress1}</div>
                <div className={styles.billToLine}>{customer.BillToAddress2}</div>
                <div className={styles.billToLine}>
                    {customer.BillToCity}
                    {customer.BillToCity && ','} {customer.BillToState} {customer.BillToZipcode}
                </div>
                <div className={styles.billToLine} style={{ paddingBottom: 10 }}>
                    {customer.BillToCountry ? COUNTRY_CODES[customer.BillToCountry.trim()] : ''}
                </div>
            </div>
            <div>
                <div
                    className={styles.shipToRow}
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                    <div className={styles.lightText}>Ship to:</div>

                    {reviewingQuoteOutput && <div></div>}
                </div>
                <div className={styles.customerLine}>
                    <TextOverflow text={orderShipTo.ShipToName || ''} maxWidth={220} />
                </div>
                <div className={styles.billToLine}>{orderShipTo.ShipToAddress1}</div>
                <div className={styles.billToLine}>{orderShipTo.ShipToAddress2}</div>
                <div className={styles.billToLine}>
                    {orderShipTo.ShipToCity}
                    {orderShipTo.ShipToCity && ','} {orderShipTo.ShipToState}{' '}
                    {orderShipTo.ShipToZipcode}
                </div>
                <div className={styles.billToLine}>
                    {COUNTRY_CODES[orderShipTo.ShipToCountry!.trim()]}
                </div>
            </div>
            <div className={styles.quoteMetaInfoSection}>
                <div className={styles.quoteMetaInfo}>
                    <div style={{ fontWeight: 500 }}>Quoted By:</div>

                    <HoverIcon
                        growDown
                        growLeft
                        renderIcon={() => (
                            <div
                                className="centered"
                                style={{ height: '100%', justifyContent: 'start' }}
                            >
                                <div style={{ fontWeight: 300, whiteSpace: 'nowrap' }}>
                                    {quoteUser?.Firstname.trim()} {quoteUser?.Lastname.trim()}&nbsp;
                                </div>
                                <span className={'a-i-info'} style={{ marginBottom: 1 }}></span>
                            </div>
                        )}
                    >
                        {(closeBubble: () => void) => (
                            <div
                                className={styles.identifierBubble}
                                style={{
                                    width: 'max-content',
                                    display: 'grid',
                                    gridTemplateColumns: 'max-content max-content',
                                }}
                            >
                                <div className={styles.lightText}>Email:</div>
                                <div style={{ color: 'var(--linkblue)' }}>{quoteUser?.Email}</div>
                                <div className={styles.lightText}>Phone:</div>
                                <div className={styles.lightText}>
                                    {quoteUser?.TelephoneNumber ? quoteUser?.TelephoneNumber : ''}
                                </div>
                                <div className={styles.lightText}>Address:</div>
                                <div
                                    className={styles.lightText}
                                    // style={{ whiteSpace: 'nowrap', position: 'relative' }}
                                >
                                    <div style={{ display: 'block' }}>{warehouse?.name}</div>
                                    <div style={{ display: 'block' }}>{warehouse?.addr1}</div>
                                    <div style={{ display: 'block' }}>
                                        {warehouse?.addr2} {warehouse?.addr3}
                                    </div>
                                    <div style={{ display: 'block' }}>
                                        {warehouse?.city}, {warehouse?.state} {warehouse?.zip}
                                    </div>
                                </div>
                            </div>
                        )}
                    </HoverIcon>
                </div>
                <div className={styles.quoteMetaInfo2}>
                    <div style={{ fontWeight: 500 }}>Reference:</div>
                    <div>
                        <div
                            className={styles.lightText}
                            style={{
                                // paddingRight: 100,
                                maxWidth: 370,
                            }}
                            data-testid="referenceNumber"
                        >
                            <TextOverflow
                                text={order.OrderHeaderView[0].RFQNumber || ''}
                                maxWidth={150}
                                bubbleMinWidth={250}
                            />
                        </div>
                    </div>
                    <div style={{ fontWeight: 500 }}>AWC Quote #:</div>
                    <div className={styles.lightText} data-testid="quoteNumber">
                        {orderHeader[0].OrderNumber === '9999999999'
                            ? 'New'
                            : orderHeader[0].OrderNumber}
                    </div>
                    <div style={{ fontWeight: 500 }}>Quote Version:</div>
                    {/* TODO: determine quote version */}
                    <div className={styles.lightText} data-testid="quoteVersion">
                        {(orderHeader[0].Revision || 0).toString().padStart(3, '0')}
                    </div>
                    <div style={{ fontWeight: 500 }}>Quote Date:</div>
                    <div className={styles.lightText}>
                        {orderHeader[0].OrderDate
                            ? new Date(orderHeader[0].OrderDate).toLocaleDateString()
                            : ''}
                    </div>
                    <div style={{ fontWeight: 500 }}>Valid Until:</div>
                    <div className={styles.lightText}>
                        {orderHeader[0].QuoteExpirationDate
                            ? new Date(orderHeader[0].QuoteExpirationDate).toLocaleDateString()
                            : ''}
                    </div>
                    <div style={{ fontWeight: 500 }}>AWC Account #:</div>
                    <div className={styles.lightText}>{customer.AccountCode}</div>
                </div>
            </div>
            <div className={clsx([!hasValidPart && styles.fadedText, styles.quoteReviewContainer])}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <AWCButton
                        type={BUTTON_TYPES.PRIMARY}
                        style={{
                            marginBottom: '5%',
                            width: '45%',
                            padding: '6px 0px',
                        }}
                        onClick={onUpdateClick}
                    >
                        Update
                    </AWCButton>

                    <AWCButton
                        type={'secondary'}
                        style={{
                            marginBottom: '5%',
                            width: '45%',
                            padding: '6px 0px',
                        }}
                        onClick={onNewClick}
                    >
                        New Quote
                    </AWCButton>
                </div>
                <div className={styles.shippingMethodShortString}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '2%',
                        }}
                    >
                        <div style={{ fontWeight: 500 }}>Shipping Method:</div>
                        <div>
                            <HoverIcon
                                growDown
                                growLeft
                                renderIcon={() => (
                                    <div
                                        className="centered"
                                        style={{ height: '100%', justifyContent: 'space-between' }}
                                    >
                                        <div
                                            style={{
                                                fontWeight: 300,
                                                whiteSpace: 'nowrap',
                                                marginBottom: 5,
                                            }}
                                        >
                                            {shippingCarrier}&nbsp;
                                        </div>
                                        <span
                                            className={'a-i-info'}
                                            style={{ marginBottom: 5, height: 10, width: 10 }}
                                        ></span>
                                    </div>
                                )}
                            >
                                {(closeBubble: () => void) => (
                                    <div className={styles.identifierBubble}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                marginBottom: 5,
                                            }}
                                        >
                                            <div className={styles.shippingMethodLabel}>
                                                Shipping Method:
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                whiteSpace: 'nowrap',
                                                marginTop: 5,
                                            }}
                                        >
                                            <div className={styles.shippingMethodLabel}>
                                                Carrier:&nbsp;
                                            </div>
                                            <div className={styles.shippingMethodValue}>
                                                {shippingCarrier}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
                                            <div className={styles.shippingMethodLabel}>
                                                Service:&nbsp;
                                            </div>
                                            <div className={styles.shippingMethodValue}>
                                                {shippingService}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
                                            <div className={styles.shippingMethodLabel}>
                                                Terms:&nbsp;
                                            </div>
                                            <div className={styles.shippingMethodValue}>
                                                {shippingTerms}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
                                            <div className={styles.shippingMethodLabel}>
                                                Account #:&nbsp;
                                            </div>
                                            <div className={styles.shippingMethodValue}>
                                                {shippingCollectAccount}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </HoverIcon>
                        </div>
                    </div>
                </div>

                <div className={styles.divider} style={{ marginBottom: '4%' }} />
                <div
                    style={{
                        fontSize: 14,
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    Subtotal:{' '}
                    <span>
                        {orderMiscInfo &&
                            hasValidPart &&
                            orderMiscInfo.OrderValue.SubTotal.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            })}
                    </span>
                </div>
                <div
                    style={{
                        fontSize: 14,
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <div>Est. Freight: </div>
                    {/* TODO this is same as in orderheader */}
                    {orderMiscInfo && hasValidPart ? (
                        orderMiscInfo.OrderValue.FreightErrorMessage ? (
                            <>
                                <div></div>
                                <HoverIcon
                                    growLeft
                                    growUp
                                    renderIcon={() => (
                                        <div style={{ fontWeight: 300 }}>
                                            TBD&nbsp;<span className={'a-i-info-small'}></span>
                                        </div>
                                    )}
                                >
                                    {(closeBubble: () => void) =>
                                        orderMiscInfo.OrderValue.FreightErrorMessage && (
                                            <div
                                                className={styles.identifierBubble}
                                                style={{ width: 270 }}
                                            >
                                                <div>
                                                    {orderMiscInfo.OrderValue.FreightErrorMessage}
                                                </div>
                                            </div>
                                        )
                                    }
                                </HoverIcon>
                            </>
                        ) : (
                            <>
                                <div>${orderMiscInfo.OrderValue.FreightEstimate.toFixed(2)}</div>
                            </>
                        )
                    ) : (
                        <>
                            <div></div>
                            <div></div>
                        </>
                    )}
                </div>
                <div
                    style={{
                        marginBottom: '2%',
                        fontSize: 14,
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    Est. Tax:{' '}
                    <span>
                        {orderMiscInfo &&
                            hasValidPart &&
                            orderMiscInfo.OrderValue.TaxAmt.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            })}
                    </span>
                </div>
                <div className={styles.divider} style={{ marginBottom: '6%' }} />
                <div
                    className={styles.orderTotal}
                    style={{
                        marginBottom: '2%',
                        fontSize: 16,
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    Order Total:{' '}
                    <span>
                        {orderMiscInfo &&
                            hasValidPart &&
                            orderMiscInfo.OrderValue.Total.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            })}
                    </span>
                </div>
            </div>
            {updateClosedOpen && (
                <UpdateClosedDialog
                    onCancel={() => setUpdateClosedOpen(false)}
                    onNewQuote={() => {
                        setLoadOptionsOpen({ ...updateClosedOpen, mode: 'copy' })
                        setUpdateClosedOpen(false)
                    }}
                />
            )}
            {loadOptionsOpen && (
                <LoadOptionsDialog
                    title={
                        loadOptionsOpen.mode === 'copy'
                            ? `New Quote Using BOM from Quote ${order.OrderHeaderView[0].OrderNumber}`
                            : `Update Quote ${order.OrderHeaderView[0].OrderNumber}`
                    }
                    errorText={
                        legacyAvailabilityModes.includes(order.OrderHeaderView[0].AvailabilityMode)
                            ? 'Fast quote does not support the availability mode on this quote. Quote will open with Estimated Delivery Date.'
                            : ''
                    }
                    availabilityMode={order.OrderHeaderView[0].AvailabilityMode}
                    onSubmit={async ({
                        UpdateAvailability,
                        UpdatePricing,
                    }: {
                        UpdateAvailability: boolean
                        UpdatePricing: boolean
                    }) => {
                        let shouldLoad = canBeOpenedInFQ(
                            order.OrderHeaderView[0],
                            loadOptionsOpen.mode === 'copy'
                        )

                        if (loadOptionsOpen.mode === 'update') {
                            if (shouldLoad) {
                                if (
                                    legacyAvailabilityModes.includes(
                                        order.OrderHeaderView[0].AvailabilityMode
                                    )
                                ) {
                                    dispatch(updateAvailabilityMode('C'))
                                }
                                dispatch(
                                    updateOrder({
                                        IncludeMiscInfo: true,
                                        OrderNumber: order.OrderHeaderView[0].OrderNumber,
                                        CalculateFreight: true,
                                        CalculateTax: true,
                                        UpdatePricing,
                                        UpdateAvailability,
                                    })
                                )
                            } else {
                                window.open(
                                    `${getBaseURL()}masterclient/main.aspx?OpenDialog=OrderEntry&OrderNumber=${
                                        order.OrderHeaderView[0].OrderNumber
                                    }&UpdatePricing=${UpdatePricing}&UpdateAvailability=${UpdateAvailability}`
                                )
                            }
                        }
                        if (loadOptionsOpen.mode === 'copy') {
                            if (
                                legacyAvailabilityModes.includes(
                                    order.OrderHeaderView[0].AvailabilityMode
                                )
                            ) {
                                dispatch(updateAvailabilityMode('C'))
                            }
                            let orderNumber = await dispatch(
                                copyOrder({
                                    OrderNumber: order.OrderHeaderView[0].OrderNumber,
                                    ContactKey: currentRFQToContact.ContactKey,
                                    CustomerAccountCode:
                                        currentRFQToCustomer.AccountCode.trim() !== ''
                                            ? currentRFQToCustomer.AccountCode.trim()
                                            : order.OrderHeaderView[0].CustomerAccountCode,
                                    UpdateAvailability,
                                    UpdatePricing,
                                    load: shouldLoad,
                                    // need this to save to db so we can get real order number to load if we aren't loading in FQ
                                    SaveCopy: !shouldLoad,
                                })
                            )
                            if (!shouldLoad) {
                                window.open(
                                    `${getBaseURL()}masterclient/main.aspx?OpenDialog=OrderEntry&OrderNumber=${orderNumber}`
                                )
                            }
                        }
                        setLoadOptionsOpen(false)
                        // close()
                        onSubmit()
                    }}
                    close={() => setLoadOptionsOpen(false)}
                    canOpenInFQ={canBeOpenedInFQ(
                        order.OrderHeaderView[0],
                        loadOptionsOpen.mode === 'copy'
                    )}
                />
            )}
        </div>
    )
}
interface OrderPreviewProps {
    orderNumber: string
    close(): void
    onSubmit(): void
    type: string
}
// think the header only depends on account, toContact
// lineitems only depend on customer and lineitems
const OrderPreview = (props: OrderPreviewProps) => {
    const { close, onSubmit, type } = props
    const [info, setInfo] = useState<
        | LOADING_STATES.INIT
        | LOADING_STATES.LOADING
        | {
              order: GetOrderReturnPayload
              customer: CustomerRow
              toContact: undefined | ContactCustomerAccountCodeViewRow
              // TODO make this thing return undefined or contactviewrow
              toContactExtended: false | ContactViewRow
              quoteUser: QuoteUserInfo
              warehouse: WarehouseReturnPayload
          }
    >(LOADING_STATES.INIT)

    const loadOrder = async (orderNumber: string) => {
        loadAttempt += 1
        let loadId = loadAttempt
        setInfo(LOADING_STATES.LOADING)
        const info = await getOrder({
            OrderNumber: orderNumber,
            IncludeMiscInfo: true,
            CalculateFreight: true,
            CalculateTax: true,
            IsPreview: true,
        })

        // TODO shouldn't all be awaited in order
        let existingContact =
            !!info.toContact?.EmailAddress &&
            !!info.toContact.EmailAddress.trim() &&
            (await fetchGetContact(info.toContact?.EmailAddress!, info.toContact?.ContactKey))
        let user: QuoteUserInfo = await fetchQuoteUser(info.order.OrderHeaderView[0].Username!)
        let warehouse: WarehouseReturnPayload = await fetchWarehouse(user.Warehouse)
        if (loadAttempt === loadId)
            setInfo({
                ...info,
                warehouse,
                quoteUser: user,
                toContactExtended: existingContact,
            })
    }
    // TODO need this to be cancelable
    useEffect(() => {
        loadOrder(props.orderNumber)
    }, [props.orderNumber])
    return info === LOADING_STATES.INIT ? (
        <></>
    ) : info === LOADING_STATES.LOADING ? (
        <div style={{ height: '100%', width: '100%' }}>
            <div className={'k-i-loading'} style={{ fontSize: 60, height: 425 }} />
        </div>
    ) : (
        <>
            <PreviewHeader {...info} type={type} close={close} onSubmit={onSubmit} />
            <div style={{ margin: '0px 5px 0px 15px' }}>
                <LineItems
                    accountCode={info.customer.AccountCode}
                    reviewingQuoteOutput={true}
                    orderDetail={info.order.OrderHeaderView[0].OrderDetailView}
                    orderHeader={info.order.OrderHeaderView[0]}
                    noNotes
                    isPreview={true}
                />
            </div>
        </>
    )
}
interface SearchQuotesModalProps {
    close(): void
    onSubmit(): void
    accountCode: string
    contactEmail: string
    filter?: { field: keyof OrderHistoryReturn[number]; value: string } | boolean
    selectedOrder?: string | null
    hideFilter?: boolean
}
const searchableVendorId = (vendorId: string) => {
    const vendorIds = getVendorIdsFromString(vendorId)
    return vendorIds.map((row) => `${row.id} ${row.name}`).join(' ')
}
const searchableString = (order: OrderHistoryReturn[number]) => {
    let newRow = { ...order }
    newRow.OrderDate = new Date(order.OrderDate).toLocaleDateString()
    let visibleKeys: (keyof OrderHistoryReturn[number])[] = [
        'OrderDate',
        'Contact',
        'OrderType',
        'CustomerReference',
        'OrderNumber',
        'UniqueCustomerName', // not actually visible,
        'VendorId',
    ]
    return visibleKeys
        .map((key) =>
            key === 'VendorId' ? searchableVendorId(newRow[key]) : newRow[key].toString()
        )
        .join(' ')
        .toLowerCase()
}
const getVendorIdsFromString = (str: string) => {
    return str
        .split('|')
        .filter((row) => row)
        .map((row) => row.split('`'))
        .map((row) => ({ id: row[0], name: row[1] }))
}
const VendorIDs = ({
    vendorId,
    search,
    isFirst,
}: {
    vendorId: string
    search: string
    isFirst: boolean
}) => {
    let vendorIds = getVendorIdsFromString(vendorId).map((row) => ({
        ...row,
        selected: search && `${row.id} ${row.name}`.toLowerCase().includes(search.toLowerCase()),
    }))
    return (
        <div>
            {vendorIds.slice(0, 4).map((row, i, arr) => (
                <span>
                    <HoverIcon
                        growLeft
                        growDown={isFirst}
                        renderIcon={() => (
                            <div className={row.selected ? styles.matchingText : ''}>{row.id}</div>
                        )}
                    >
                        {(closeBubble: () => void) => (
                            <div
                                className={styles.identifierBubble}
                                style={{ width: 'max-content' }}
                            >
                                {row.name}
                            </div>
                        )}
                    </HoverIcon>
                    {i !== arr.length - 1 ? ', ' : ''}
                    {/* only showing top 4 */}
                    {i === arr.length - 1 && arr.length < vendorIds.length ? '...' : ''}
                </span>
            ))}
        </div>
    )
}
export const SearchQuotesModal = (props: SearchQuotesModalProps) => {
    const { close, onSubmit } = props
    const history = useAppSelector((state) => state.order.orderHistory)
    const [filter, setFilter] = useState('')
    useEffect(() => {
        typeof props.filter === 'object' ? setFilter(props.filter.value) : setFilter('')
    }, [props.filter])

    let filteredHistory = history.filter((row) =>
        searchableString(row).includes(filter.toLowerCase())
    )
    let resultsNum = filteredHistory.length
    const loading = useAppSelector((state) => state.order.orderHistoryLoading)
    const [selectedOrder, setSelectedOrder] = useState<{
        index: number
        order: OrderHistoryReturn[number]
    }>()
    // const dispatch = useAppDispatch()
    useEffect(() => {
        let index = history.findIndex((row) => row.OrderNumber === props.selectedOrder)
        index !== -1 && setSelectedOrder({ index, order: history[index] })
    }, [props.selectedOrder])

    useEffect(() => {
        let filteredHistory = history.filter((row) =>
            searchableString(row).includes(filter.toLowerCase())
        )
        const handleKeypress = (e: KeyboardEvent) => {
            if (e.key === 'ArrowDown') {
                let newIndex = selectedOrder ? selectedOrder.index + 1 : 0
                newIndex = Math.min(newIndex, filteredHistory.length - 1)
                let order = filteredHistory[newIndex]
                setSelectedOrder({ index: newIndex, order })
            }
            if (e.key === 'ArrowUp') {
                let newIndex = selectedOrder ? selectedOrder.index - 1 : 0
                newIndex = Math.max(newIndex, 0)
                let order = filteredHistory[newIndex]
                setSelectedOrder({ index: newIndex, order })
            }
        }
        window.addEventListener('keydown', handleKeypress)
        return () => window.removeEventListener('keydown', handleKeypress)
    }, [selectedOrder, history, filter])
    // useEffect(() => {
    //     setLoading(true)
    //     fetchOrderHistory({
    //         contactEmailAddress: contactEmail,
    //         customerAccountCode: accountCode,
    //     }).then((data) => {
    //         setHistory(data)
    //         setLoading(false)
    //     })
    // }, [])

    const onOrderClick = (order: OrderHistoryReturn[number], index: number) => {
        // if (order.OrderEntryType !== 'FQ') {
        //     let url = `https://net.awc-inc.com/masterclient/main.aspx?OpenDialog=OrderEntry&OrderNumber=${order.OrderNumber}`
        //     window.open(url)
        // } else {
        //     setSelectedIndex(index)
        // }
        // going to show preview for non fastquotes here
        setSelectedOrder({ index, order: filteredHistory[index] })
    }
    function escapeRegExp(str: string) {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
    }

    const HighlightMatching = ({
        text,
        pattern,
        isContact,
    }: {
        text: string
        pattern: string
        isContact?: boolean
    }) => {
        const isLargeView = useMediaQuery({
            query: '(min-width: 1484px)',
        })
        const font = isLargeView ? '16px roboto' : '12px roboto'
        const [ref, bounds] = useMeasure()
        const getTextWidth = (text: string, font: string) => {
            // re-use canvas object for better performance
            const canvas = document.createElement('canvas')
            const context = canvas.getContext('2d')!
            context.font = font
            const metrics = context.measureText(text)
            return metrics.width
        }
        let overflows = bounds.width < getTextWidth(text.replace(/\s+/g, ' ').trim(), font)

        if (isContact) {
            const nameEmail = text.replace('>', '').split('<')
            return (
                <div>
                    <div style={{ display: 'inline-block' }}>
                        <TextOverflow text={nameEmail[0]} maxWidth={220} />
                    </div>
                    &nbsp;
                    <div style={{ display: 'inline-block' }} className={styles.email}>
                        <TextOverflow text={nameEmail[1]} maxWidth={220} />
                    </div>
                </div>
            )
        }
        if (!pattern) return <TextOverflow text={text || ''} maxWidth={200} />
        let regex = new RegExp(escapeRegExp(pattern.toLowerCase()), 'g')
        let ranges: { start: number; end: number }[] = []
        let r
        let currentIndex = 0
        let lastMatchIndex = 0
        while ((r = regex.exec(text.toLowerCase()))) {
            ranges.push({ start: r.index, end: regex.lastIndex })
            lastMatchIndex = regex.lastIndex
        }
        let textArray = Array.from(text)

        return (
            <div ref={ref} style={{ maxWidth: 475 }}>
                {overflows ? (
                    <HoverIcon
                        growDown={true}
                        renderIcon={() => (
                            <div
                                style={{
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {ranges.map((row, i) => {
                                    let myIndex = currentIndex
                                    currentIndex = row.end
                                    return (
                                        <div style={{ display: 'contents' }}>
                                            {textArray.slice(myIndex, row.start).join('')}
                                            <div
                                                className={styles.matchingText}
                                                style={{ display: 'inline' }}
                                            >
                                                {textArray.slice(row.start, row.end).join('')}
                                            </div>
                                        </div>
                                    )
                                })}
                                {textArray.slice(lastMatchIndex, text.length).join('')}
                            </div>
                        )}
                        style={{ display: 'block' }}
                    >
                        {(closeBubble: () => void) => (
                            <div
                                className={styles.leadTimeBubble}
                                style={{
                                    textAlign: 'left',
                                    padding: '14px 19px',
                                    minWidth: 200,
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'initial',
                                }}
                                onClick={(e: any) => e.stopPropagation()}
                            >
                                {textArray}
                            </div>
                        )}
                    </HoverIcon>
                ) : (
                    <div style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {ranges.map((row, i) => {
                            let myIndex = currentIndex
                            currentIndex = row.end
                            return (
                                <div style={{ display: 'contents' }}>
                                    {textArray.slice(myIndex, row.start).join('')}
                                    <div
                                        className={styles.matchingText}
                                        style={{ display: 'inline' }}
                                    >
                                        {textArray.slice(row.start, row.end).join('')}
                                    </div>
                                </div>
                            )
                        })}
                        {textArray.slice(lastMatchIndex, text.length).join('')}
                    </div>
                )}
            </div>
        )
    }
    const Row = ({ index, style }: any) => {
        let order = filteredHistory[index]
        return (
            <div
                style={style}
                className={clsx([
                    order.OrderNumber === selectedOrder?.order.OrderNumber &&
                        styles.orderListItemSelected,
                    styles.orderListItem,
                    // order.OrderEntryType !== 'FQ' && styles.orderListItemNotFastQuote,
                ])}
                onClick={() => onOrderClick(order, index)}
            >
                <div>
                    <HighlightMatching
                        text={new Date(order.OrderDate).toLocaleDateString()}
                        pattern={filter}
                    />
                </div>
                <div>
                    <HighlightMatching text={order.Contact} pattern={filter} isContact />
                </div>
                <div>
                    <HighlightMatching text={order.OrderType} pattern={filter} />
                </div>
                <div>
                    <HighlightMatching text={order.CustomerReference} pattern={filter} />
                </div>
                <div>
                    <HighlightMatching text={order.OrderNumber} pattern={filter} />
                </div>

                <div>
                    <VendorIDs search={filter} vendorId={order.VendorId} isFirst={index === 0} />
                </div>
            </div>
        )
    }

    return (
        <div data-testid="searchQuotesDiv">
            <div className={styles.searchQuotesModal} data-testid="searchQuotesModal">
                <div
                    style={{
                        padding: 10,
                        backgroundColor: 'var(--surface2)',
                        borderRadius: '10px 10px',
                    }}
                >
                    <div style={{ display: 'inline-block', width: 300 }}>
                        {props.hideFilter ? (
                            <span style={{ fontSize: 16 }}>
                                {filteredHistory[0].OrderType} #{filteredHistory[0].OrderNumber}
                            </span>
                        ) : (
                            <span style={{ fontSize: 16 }}>Quote / Orders Search</span>
                        )}
                        {!props.hideFilter && (
                            <span style={{ fontWeight: 300, fontSize: 12, marginLeft: 20 }}>
                                {resultsNum.toLocaleString()} Results found
                            </span>
                        )}
                    </div>

                    <div style={{ display: 'inline-block', width: 350, marginLeft: 15 }}>
                        {!props.hideFilter && (
                            <FastQuoteInput
                                Icon={FilterIcon}
                                placeholder={'Filter'}
                                value={filter}
                                autoFocus
                                onChange={(newVal: string) => {
                                    setFilter(newVal)
                                    setSelectedOrder(undefined)
                                }}
                                renderEndIcon={() =>
                                    filter && (
                                        <div
                                            style={{ width: 18, height: 15 }}
                                            className={'a-i-close'}
                                            onClick={() => {
                                                setFilter('')
                                            }}
                                        />
                                    )
                                }
                            />
                        )}
                    </div>
                    <div
                        style={{ display: 'inline-block', float: 'right' }}
                        className="a-i-close"
                        onClick={close}
                    />
                </div>

                <div className={styles.searchQuotesListContainer}>
                    {/* <Scrollbars
                        className={'scroll-bar'}
                        renderTrackHorizontal={() => <div />}
                        autoHeight
                        autoHeightMin={300}
                        autoHeightMax={300}
                        height={315}
                    > */}

                    <div className={styles.orderListHeader}>
                        <div>Date</div>
                        <div>To</div>
                        <div>Type</div>
                        <div>Customer Reference</div>
                        <div>Quote / Order #</div>
                        <div>Tech Partners</div>
                    </div>
                    {loading === LOADING_STATES.LOADING ? (
                        <div className={'centered'}>
                            <div className={'k-i-loading'} style={{ fontSize: 200, height: 315 }} />
                        </div>
                    ) : filteredHistory.length === 0 ? (
                        <div
                            style={{
                                height: 315,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <span>
                                No past orders found{' '}
                                {history.length !== 0 ? 'with this filter' : ''}
                            </span>
                        </div>
                    ) : (
                        <List
                            className="List"
                            height={175}
                            itemCount={filteredHistory.length}
                            itemSize={35}
                            width={'100%'}
                        >
                            {Row}
                        </List>
                    )}

                    <div className={styles.darkDivider} />

                    <div
                        style={{
                            display: 'flex',
                            height: 'calc(100vh - 325px)',
                        }}
                    >
                        <div style={{ flex: '1 1 auto' }}>
                            <AutoSizer>
                                {({ width, height }) => {
                                    return (
                                        <Scrollbars
                                            // className={'scroll-bar'}
                                            // renderTrackHorizontal={() => <div />}
                                            // autoHeight
                                            // autoHeightMin={550}
                                            // autoHeightMax={750}
                                            // height={650}
                                            style={{ width, height }}
                                            // style={{
                                            //     borderTop: '2px solid black',
                                            // }}
                                        >
                                            {selectedOrder ? (
                                                <OrderPreview
                                                    orderNumber={selectedOrder.order.OrderNumber}
                                                    close={close}
                                                    onSubmit={onSubmit}
                                                    type={selectedOrder.order.OrderType}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        height: 650,
                                                        width: '100%',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            justifySelf: 'center',
                                                            alignSelf: 'center',
                                                        }}
                                                    >
                                                        Select Quote to Preview
                                                    </div>
                                                </div>
                                            )}
                                        </Scrollbars>
                                    )
                                }}
                            </AutoSizer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
